import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { MapPin, Sparkles, Users, Compass, ArrowRight, Leaf, Heart } from "lucide-react";
import * as Icons from "lucide-react";
import { ACTIVITY_ICONS } from "@/lib/activities";
import FarmCard from "@/components/FarmCard";

const HERO_HEADLINES = [
  { pre: "Crescere, giocare e scoprire", accent: "nella natura" },
  { pre: "Fuori c'è un mondo", accent: "da scoprire" },
  { pre: "Dove la natura", accent: "diventa esperienza" },
  { pre: "Scopri, gioca, cresci", accent: "nella natura" },
];

export default function Landing() {
  const [featured, setFeatured] = useState([]);
  const [activities, setActivities] = useState([]);
  const [headline] = useState(() => HERO_HEADLINES[Math.floor(Math.random() * HERO_HEADLINES.length)]);

  useEffect(() => {
    api.get("/companies").then((r) => setFeatured(r.data.slice(0, 6)));
    api.get("/activities").then((r) => setActivities(r.data));
  }, []);

  return (
    <div className="bg-[#FBF9F6]">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=2000"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#2C312E]/75 via-[#2C312E]/55 to-[#4A6B53]/50" />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 md:px-10 pt-24 pb-32 md:pt-32 md:pb-44">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur border border-white/20 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-white mb-6 rise">
              <Leaf size={14} /> Network Agrigiochiamo
            </div>
            <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-7xl text-white leading-[0.95] rise rise-1" data-testid="hero-headline">
              {headline.pre}<br />
              <span className="text-[#E4B871]">{headline.accent}</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-white/85 max-w-2xl leading-relaxed rise rise-2">
              Trova la fattoria didattica o la realtà educativa in natura più vicina a te.
              Esperienze pensate per scuole e famiglie, in tutta Italia.
            </p>
            <div className="mt-10 flex flex-wrap gap-3 rise rise-3">
              <Link
                to="/fattorie"
                className="inline-flex items-center gap-2 rounded-full bg-[#C8795A] text-white px-8 py-4 text-base font-bold shadow-md hover:bg-[#B3684B] hover:-translate-y-0.5 transition-all"
                data-testid="cta-explore"
              >
                <MapPin size={18} /> Esplora le fattorie
              </Link>
              <Link
                to="/fattorie?near=me"
                className="inline-flex items-center gap-2 rounded-full bg-white text-[#2C312E] px-8 py-4 text-base font-bold hover:-translate-y-0.5 transition-all"
                data-testid="cta-nearme"
              >
                <Compass size={18} /> Vicino a me
              </Link>
            </div>
          </div>

          {/* stats */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 max-w-4xl rise rise-4">
            {[
              { n: featured.length > 0 ? "14+" : "—", l: "Fattorie nel network" },
              { n: "9", l: "Tipologie di attività" },
              { n: "10+", l: "Regioni italiane" },
              { n: "100%", l: "Educazione in natura" },
            ].map((s, i) => (
              <div key={i} className="rounded-2xl bg-white/10 backdrop-blur border border-white/15 p-5">
                <div className="font-display font-black text-3xl md:text-4xl text-white">{s.n}</div>
                <div className="text-xs md:text-sm text-white/75 font-semibold mt-1">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ACTIVITY TYPES */}
      <section className="mx-auto max-w-7xl px-6 md:px-10 py-20 md:py-28">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-4">
          <div className="max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#C8795A] mb-3">Cosa puoi vivere</div>
            <h2 className="font-display font-black text-3xl md:text-5xl text-[#2C312E] leading-tight">
              Tante esperienze.<br />Un unico filo: la natura.
            </h2>
          </div>
          <p className="text-[#58625B] md:max-w-sm">
            Filtra le proposte per tipologia di attività e trova ciò che fa per la tua classe o la tua famiglia.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4">
          {activities.map((a, i) => {
            const Icon = Icons[ACTIVITY_ICONS[a.slug]] || Sparkles;
            return (
              <Link
                key={a.slug}
                to={`/fattorie?activity=${a.slug}`}
                className="group relative overflow-hidden rounded-3xl bg-white border border-[#E5DCD0] p-6 md:p-8 hover:border-[#C8795A]/40 hover:shadow-lg transition-all rise"
                style={{ animationDelay: `${i * 0.04}s` }}
                data-testid={`activity-card-${a.slug}`}
              >
                <div className="w-12 h-12 rounded-2xl bg-[#F2EBE1] flex items-center justify-center text-[#4A6B53] group-hover:bg-[#4A6B53] group-hover:text-white transition-colors mb-4">
                  <Icon size={22} strokeWidth={2.2} />
                </div>
                <div className="font-display font-extrabold text-lg text-[#2C312E]">{a.name}</div>
                <ArrowRight size={16} className="absolute bottom-6 right-6 text-[#C8795A] opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
              </Link>
            );
          })}
        </div>
      </section>

      {/* FEATURED FARMS */}
      <section className="bg-[#F2EBE1] py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 gap-4">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#C8795A] mb-3">Fattorie in evidenza</div>
              <h2 className="font-display font-black text-3xl md:text-5xl text-[#2C312E] leading-tight">
                Luoghi veri,<br />persone genuine e la natura buona maestra.
              </h2>
            </div>
            <Link
              to="/fattorie"
              className="inline-flex items-center gap-2 text-[#4A6B53] font-bold text-sm hover:underline"
              data-testid="view-all-farms"
            >
              Vedi tutte <ArrowRight size={16} />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {featured.map((f) => <FarmCard key={f.id} farm={f} />)}
          </div>
        </div>
      </section>

      {/* FOR WHO */}
      <section className="mx-auto max-w-7xl px-6 md:px-10 py-20 md:py-28 grid md:grid-cols-2 gap-8">
        <div className="relative rounded-[2rem] bg-[#4A6B53] text-white p-10 md:p-14 overflow-hidden">
          <Users size={40} className="mb-6 text-[#E4B871]" />
          <h3 className="font-display font-black text-3xl md:text-4xl leading-tight">Per le scuole</h3>
          <p className="mt-4 text-white/85 leading-relaxed">
            Uscite didattiche, progetti di outdoor education, campi estivi con laboratori
            e osservazione degli animali. Tutto il Network in un unico posto.
          </p>
          <Link
            to="/fattorie"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#E4B871] text-[#2C312E] px-6 py-3 text-sm font-bold hover:-translate-y-0.5 transition-all"
          >
            Trova una fattoria <ArrowRight size={16} />
          </Link>
        </div>
        <div className="relative rounded-[2rem] bg-[#C8795A] text-white p-10 md:p-14 overflow-hidden">
          <Heart size={40} className="mb-6 text-white" />
          <h3 className="font-display font-black text-3xl md:text-4xl leading-tight">Per le famiglie</h3>
          <p className="mt-4 text-white/90 leading-relaxed">
            Weekend in campagna, feste di compleanno, pranzi a km zero,
            pic nic e domeniche a contatto con gli animali. Scegli, prenota e vai.
          </p>
          <Link
            to="/register"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-white text-[#C8795A] px-6 py-3 text-sm font-bold hover:-translate-y-0.5 transition-all"
          >
            Crea un account <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* JOIN NETWORK CTA */}
      <section className="mx-auto max-w-7xl px-6 md:px-10 pb-20">
        <div className="relative rounded-[2.5rem] bg-[#2C312E] text-white p-10 md:p-16 overflow-hidden">
          <div className="absolute -right-24 -top-24 w-96 h-96 rounded-full bg-[#4A6B53]/30 blur-3xl"/>
          <div className="absolute -left-32 -bottom-32 w-96 h-96 rounded-full bg-[#C8795A]/20 blur-3xl"/>
          <div className="relative grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-[#E4B871] mb-3">Campagna Soci 2026</div>
              <h2 className="font-display font-black text-3xl md:text-5xl leading-tight">
                Sei una fattoria o una realtà educativa?
              </h2>
              <p className="mt-5 text-white/80 leading-relaxed max-w-lg">
                Unisciti al Network Agrigiochiamo: gruppi di lavoro, formazione, kit associativo
                e visibilità per la tua azienda presso scuole e famiglie di tutta Italia.
              </p>
            </div>
            <div className="md:text-right">
              <div className="inline-flex items-center gap-4 rounded-3xl bg-white/10 border border-white/15 backdrop-blur p-5 mb-6">
                <div className="text-left">
                  <div className="text-xs uppercase tracking-wider text-white/60 font-bold">Quota</div>
                  <div className="font-display font-black text-3xl">€120 / €100</div>
                  <div className="text-xs text-white/70">Nuovi soci / Rinnovo</div>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row md:justify-end gap-3">
                <Link to="/adesione" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#E4B871] text-[#2C312E] px-7 py-4 font-bold hover:-translate-y-0.5 transition-all" data-testid="landing-join-cta">
                  Iscriviti al Network <ArrowRight size={16}/>
                </Link>
                <Link to="/chi-siamo#manifesto" className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-white/25 text-white px-6 py-4 font-bold hover:bg-white/10 transition-all" data-testid="landing-manifesto-cta">
                  Il Manifesto
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
